import { Input } from "@ant-design/react-native";
import Icon from "@shared/components/md-design-system/Icon/Icon";
import { colorPalette } from "@shared/config/colorsConfig";
import AndroidSpeechRecognizer, { SpeechResultEvent } from "app/services/AndroidSpeechRecognizer";
import { ToastNotifications } from "app/services/inAppToastNotifications";
import { useCallback, useEffect, useRef, useState } from "react";
import { StyleProp, TextStyle, TouchableOpacity, View } from "react-native";
import { PERMISSIONS, RESULTS, request } from "react-native-permissions";

const { TextArea } = Input;

interface VoiceMessages {
  permissionDenied?: string;
  notAvailable?: string;
  error?: string;
}

interface VoiceTextAreaProps {
  value?: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  rows?: number;
  textAreaStyle?: StyleProp<TextStyle>;
  containerClassName?: string;
  voiceToTextEnabled?: boolean;
  voiceLocale?: string;
  voiceMessages?: VoiceMessages;
  onBeforeVoiceStart?: () => void | Promise<void>;
  onVoiceStart?: () => void;
  onVoiceEnd?: () => void;
  onVoiceError?: () => void;
  onVoiceStateChange?: (isActive: boolean) => void;
}

export const VoiceTextArea = ({
  value,
  onChangeText,
  placeholder,
  rows = 3,
  textAreaStyle,
  containerClassName = "border border-gray-300 rounded-lg p-3 relative",
  voiceToTextEnabled = false,
  voiceLocale = "en-US",
  voiceMessages,
  onBeforeVoiceStart,
  onVoiceStart,
  onVoiceEnd,
  onVoiceError,
  onVoiceStateChange,
}: VoiceTextAreaProps) => {
  const [isDictating, setIsDictating] = useState(false);
  const isDictatingRef = useRef(false);
  const manualStopRequestedRef = useRef(false);
  const committedTextRef = useRef("");
  const liveTextRef = useRef("");

  useEffect(() => {
    isDictatingRef.current = isDictating;
    onVoiceStateChange?.(isDictating);
  }, [isDictating, onVoiceStateChange]);

  const showVoiceMessage = (message?: string) => {
    if (!message) {
      return;
    }

    ToastNotifications.displayErrorNotification(message);
  };

  const joinTexts = (base: string, tail: string) => {
    const normalizedBase = base.trim();
    const normalizedTail = tail.trim();

    if (!normalizedBase) {
      return normalizedTail;
    }

    if (!normalizedTail) {
      return normalizedBase;
    }

    return `${normalizedBase} ${normalizedTail}`;
  };

  const applyTranscript = useCallback((transcript: string, mode: "partial" | "final") => {
    if (!isDictatingRef.current) {
      return;
    }

    const normalizedTranscript = transcript.trim();

    if (mode === "partial") {
      if (!normalizedTranscript) {
        return;
      }
      liveTextRef.current = normalizedTranscript;
    } else {
      const finalChunk = normalizedTranscript || liveTextRef.current;
      if (!finalChunk) {
        return;
      }
      committedTextRef.current = joinTexts(committedTextRef.current, finalChunk);
      liveTextRef.current = "";
    }

    onChangeText(joinTexts(committedTextRef.current, liveTextRef.current));
  }, [onChangeText]);

  useEffect(() => {
    if (!voiceToTextEnabled) {
      return;
    }

    const resultSubscription = AndroidSpeechRecognizer.addListener("onResult", (event?: SpeechResultEvent) => {
      applyTranscript(event?.value || "", "final");
    });

    const partialResultSubscription = AndroidSpeechRecognizer.addListener("onPartialResult", (event?: SpeechResultEvent) => {
      applyTranscript(event?.value || "", "partial");
    });

    const endSubscription = AndroidSpeechRecognizer.addListener("onEnd", () => {
      if (!isDictatingRef.current) {
        return;
      }

      manualStopRequestedRef.current = false;
      setIsDictating(false);
      committedTextRef.current = "";
      liveTextRef.current = "";
      onVoiceEnd?.();
    });

    const errorSubscription = AndroidSpeechRecognizer.addListener("onError", () => {
      if (!isDictatingRef.current) {
        return;
      }

      if (manualStopRequestedRef.current) {
        manualStopRequestedRef.current = false;
        setIsDictating(false);
        committedTextRef.current = "";
        liveTextRef.current = "";
        onVoiceEnd?.();
        return;
      }

      showVoiceMessage(voiceMessages?.error);
      setIsDictating(false);
      committedTextRef.current = "";
      liveTextRef.current = "";
      onVoiceError?.();
    });

    return () => {
      resultSubscription.remove();
      partialResultSubscription.remove();
      endSubscription.remove();
      errorSubscription.remove();
    };
  }, [applyTranscript, onVoiceEnd, onVoiceError, voiceMessages?.error, voiceToTextEnabled]);

  const ensureSpeechPermissions = async () => {
    try {
      const recordAudioPermission = PERMISSIONS.ANDROID.RECORD_AUDIO;
      return (await request(recordAudioPermission)) === RESULTS.GRANTED;
    } catch {
      return false;
    }
  };

  const startVoice = async () => {
    const locales = [voiceLocale, "en-US"].filter((locale, index, all) => all.indexOf(locale) === index);

    let lastError: unknown;

    for (const locale of locales) {
      try {
        const started = await AndroidSpeechRecognizer.start(locale);
        if (!started) {
          throw new Error();
        }
        return;
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError;
  };

  const stopVoice = async () => {
    try {
      await AndroidSpeechRecognizer.stop();
    } catch {
      // Ignore stop errors when recognizer is not active.
    }
  };

  useEffect(() => {
    return () => {
      if (isDictatingRef.current) {
        stopVoice();
      }
    };
  }, []);

  const toggleDictation = async () => {
    if (isDictating) {
      manualStopRequestedRef.current = true;
      await stopVoice();
      return;
    }

    try {
      await onBeforeVoiceStart?.();
    } catch {
      showVoiceMessage(voiceMessages?.error);
      onVoiceError?.();
      return;
    }

    const hasPermissions = await ensureSpeechPermissions();
    if (!hasPermissions) {
      showVoiceMessage(voiceMessages?.permissionDenied);
      return;
    }

    const isAvailable = await AndroidSpeechRecognizer.isAvailable();
    if (!isAvailable) {
      showVoiceMessage(voiceMessages?.notAvailable);
      return;
    }

    try {
      await stopVoice();
      committedTextRef.current = (value || "").trim();
      liveTextRef.current = "";
      await startVoice();
      setIsDictating(true);
      onVoiceStart?.();
    } catch {
      showVoiceMessage(voiceMessages?.notAvailable);
      setIsDictating(false);
    }
  };

  return (
    <View className={containerClassName}>
      <TextArea
        rows={rows}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        style={textAreaStyle}
      />

      {voiceToTextEnabled && (
        <TouchableOpacity
          className="absolute bottom-2 right-2 w-8 h-8 items-center justify-center"
          activeOpacity={0.8}
          onPress={toggleDictation}
        >
          <Icon
            name={isDictating ? "pause-circle" : "audio"}
            size={18}
            color={colorPalette.primary[600]}
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

export default VoiceTextArea;
